<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  ticket: { type: Object, required: true },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const statusClass = computed(() => {
  const map = {
    OPEN: 'bg-blue-100 text-blue-700',
    ONGOING: 'bg-yellow-100 text-yellow-700',
    CLOSED: 'bg-gray-100 text-gray-600',
    RESOLVED: 'bg-green-100 text-green-700',
  };
  return map[props.ticket?.status] || 'bg-gray-100 text-gray-600';
});

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function toSentenceCase(str) {
  if (!str) return '—';
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase());
}

function openDocument(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
</script>

<template>
  <div class="fixed inset-0 z-40 flex justify-end">
    <div class="absolute inset-0 bg-black/20" @click="emit('close')" />
    <div
      class="relative z-50 w-full max-w-2xl h-full bg-white dark:bg-n-solid-2 shadow-2xl flex flex-col overflow-hidden"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-4 border-b border-n-weak flex-shrink-0"
      >
        <div>
          <p class="text-xs text-n-slate-11">
            {{ t('ARDHISASA.TICKETS.DETAIL.TITLE') }}
          </p>
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ ticket?.reference_number || '—' }}
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <span
            v-if="ticket?.status"
            class="text-xs font-medium px-2 py-0.5 rounded-full"
            :class="statusClass"
          >
            {{ ticket.status }}
          </span>
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <span
          class="i-lucide-loader-circle size-6 text-n-slate-11 animate-spin"
        />
      </div>

      <div v-else class="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <!-- Core info -->
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.SUBJECT') }}
            </p>
            <p class="text-n-slate-12 font-medium">
              {{ ticket.subject || '—' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.TYPE') }}
            </p>
            <p class="text-n-slate-12">{{ ticket.ticket_type || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.ENTITY') }}
            </p>
            <p class="text-n-slate-12">{{ ticket.entity || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.DEPARTMENT') }}
            </p>
            <p class="text-n-slate-12">{{ ticket.department || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.SOURCE') }}
            </p>
            <p class="text-n-slate-12">{{ ticket.source || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.DATE_OPENED') }}
            </p>
            <p class="text-n-slate-12">{{ formatDate(ticket.date_opened) }}</p>
          </div>
          <div v-if="ticket.date_closed">
            <p class="text-xs text-n-slate-11 mb-0.5">
              {{ t('ARDHISASA.TICKETS.DETAIL.DATE_CLOSED') }}
            </p>
            <p class="text-n-slate-12">{{ formatDate(ticket.date_closed) }}</p>
          </div>
        </div>

        <!-- Customer -->
        <div v-if="ticket.customer_name" class="border-t border-n-weak pt-4">
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-2"
          >
            {{ t('ARDHISASA.TICKETS.DETAIL.CUSTOMER') }}
          </p>
          <p class="text-sm text-n-slate-12 font-medium">
            {{ ticket.customer_name }}
          </p>
          <p
            v-if="ticket.applicant_id?.phonenum"
            class="text-xs text-n-slate-11"
          >
            {{ ticket.applicant_id.phonenum }}
          </p>
        </div>

        <!-- Assignee -->
        <div v-if="ticket.assignee" class="border-t border-n-weak pt-4">
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-2"
          >
            {{ t('ARDHISASA.TICKETS.DETAIL.ASSIGNEE') }}
          </p>
          <p class="text-sm text-n-slate-12 font-medium">
            {{ ticket.assignee.names }}
          </p>
          <p class="text-xs text-n-slate-11">{{ ticket.assignee.phonenum }}</p>
        </div>

        <!-- Description -->
        <div class="border-t border-n-weak pt-4">
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-2"
          >
            {{ t('ARDHISASA.TICKETS.DETAIL.INFORMATION') }}
          </p>
          <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
            {{ ticket.information || '—' }}
          </p>
        </div>

        <!-- Parcels -->
        <div
          v-if="ticket.parcels && ticket.parcels.length"
          class="border-t border-n-weak pt-4"
        >
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-2"
          >
            {{ t('ARDHISASA.TICKETS.DETAIL.PARCELS') }}
          </p>
          <div class="flex flex-wrap gap-1">
            <span
              v-for="parcel in ticket.parcels"
              :key="parcel"
              class="text-xs bg-n-alpha-2 px-2 py-0.5 rounded"
            >
              {{ parcel }}
            </span>
          </div>
        </div>

        <!-- Remarks -->
        <div
          v-if="ticket.remarks && ticket.remarks.length"
          class="border-t border-n-weak pt-4"
        >
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-3"
          >
            {{ t('ARDHISASA.TICKETS.DETAIL.REMARKS') }}
          </p>
          <div class="space-y-3">
            <div
              v-for="remark in ticket.remarks"
              :key="remark.id"
              class="rounded-lg p-3 text-sm"
              :class="
                remark.remark_user === 'STAFF_REMARKS'
                  ? 'bg-amber-50 dark:bg-amber-900/20'
                  : 'bg-n-alpha-2'
              "
            >
              <div class="flex items-center justify-between mb-1">
                <span class="font-medium text-n-slate-12 text-xs">{{
                  remark.actor_details?.names
                }}</span>
                <span class="text-xs text-n-slate-11">{{
                  formatDate(remark.date_created)
                }}</span>
              </div>
              <div class="flex items-center gap-3 mb-1.5">
                <span class="text-xs text-n-slate-11">
                  {{ toSentenceCase(remark.remark_type) }}
                </span>
                <span class="text-n-slate-11 text-xs">·</span>
                <span class="text-xs text-n-slate-11">
                  {{ toSentenceCase(remark.remark_user) }}
                </span>
              </div>
              <p class="text-n-slate-11">
                {{
                  Array.isArray(remark.remark)
                    ? remark.remark.join(' ')
                    : remark.remark
                }}
              </p>
              <!-- Remark documents -->
              <div
                v-if="remark.remark_documents && remark.remark_documents.length"
                class="mt-2 flex flex-wrap gap-1.5"
              >
                <button
                  v-for="doc in remark.remark_documents"
                  :key="doc.id"
                  class="flex items-center gap-1 text-xs px-2 py-1 rounded bg-white dark:bg-n-solid-3 border border-n-weak text-[#71481d] hover:bg-amber-50 transition-colors"
                  @click="openDocument(doc.document)"
                >
                  <span class="i-lucide-paperclip size-3" />
                  {{ doc.document_name || 'Document' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Activity -->
        <div
          v-if="ticket.ticket_events && ticket.ticket_events.length"
          class="border-t border-n-weak pt-4"
        >
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-3"
          >
            {{ t('ARDHISASA.TICKETS.DETAIL.EVENTS') }}
          </p>
          <div class="space-y-2">
            <div
              v-for="event in ticket.ticket_events"
              :key="event.id"
              class="flex items-start gap-2 text-xs text-n-slate-11"
            >
              <span
                class="i-lucide-circle-dot size-3 mt-0.5 flex-shrink-0 text-[#71481d]"
              />
              <div>
                <span class="font-medium text-n-slate-12">{{
                  event.activity_name
                }}</span>
                <span class="mx-1">·</span>
                <span>{{ event.actor_details?.names }}</span>
                <span class="mx-1">·</span>
                <span>{{ formatDate(event.date_created) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
